import * as THREE from 'three';

export interface RendererContext {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  canvas: HTMLCanvasElement;
}

export function createRenderer(canvas: HTMLCanvasElement): RendererContext {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    alpha: false,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: true,
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NoToneMapping;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);

  // Fixed 1000-unit world — the project canvas occupies this space.
  // Frustum is updated by resizeRenderer to letterbox correctly.
  const camera = new THREE.OrthographicCamera(
    -500, 500,
    500, -500,
    0.1, 1000,
  );
  camera.position.set(0, 0, 5);
  camera.lookAt(0, 0, 0);

  return { renderer, scene, camera, canvas };
}

/**
 * 3-layer letterboxing: container → project canvas → video source.
 *
 * The project canvas (defined by projectAspect) is always fully visible,
 * centered in the container with black bars where needed.
 *
 * @param projectAspect - 0 means "original" (use video source aspect).
 *                        Otherwise width/height ratio (e.g. 16/9, 9/16, 1).
 */
export function resizeRenderer(
  ctx: RendererContext,
  width: number,
  height: number,
  projectAspect: number = 16 / 9,
) {
  // The project canvas occupies a 1000-unit tall region in world space.
  // Its width in world units = 1000 * projectAspect.
  const canvasW = 1000 * projectAspect; // world units
  const canvasH = 1000;                 // world units

  const containerAspect = width / height;

  // We want the entire project canvas visible. Scale the camera frustum
  // so the project canvas fits, then expand in the other axis for the container.
  let frustumW: number;
  let frustumH: number;

  if (containerAspect > projectAspect) {
    // Container is wider than project → project fills height, extra space on sides
    frustumH = canvasH;
    frustumW = canvasH * containerAspect;
  } else {
    // Container is taller than project → project fills width, extra space top/bottom
    frustumW = canvasW;
    frustumH = canvasW / containerAspect;
  }

  ctx.camera.left = -frustumW / 2;
  ctx.camera.right = frustumW / 2;
  ctx.camera.top = frustumH / 2;
  ctx.camera.bottom = -frustumH / 2;
  ctx.camera.updateProjectionMatrix();

  ctx.renderer.setSize(width, height, true);
}

export function disposeRenderer(ctx: RendererContext) {
  ctx.renderer.dispose();
  ctx.scene.clear();
}
